<template>
  <div class="footer">
    <div class="footer__lines">
      <div class="footer__lines-text">
        Строк в странице
      </div>
      <input
        v-model="takeField"
        class="take-field"
        @keypress.enter="emitTake"
        @blur="emitTake"
        @input="debounceEmitTake"
      >
    </div>
    <div
      v-if="tableStore.filteredListData.length"
      class="pagination"
    >
      <button
        v-if="totalPages > 1"
        class="move__btn back"
        :disabled="isFirstPage"
        @click="changePage(currentPage - 1)"
      >
        <ChevronRight />
      </button>

      <button
        class="pagination__item"
        :class="{ active: currentPage === 1 }"
        @click="changePage(1)"
      >
        1
      </button>

      <div
        v-if="showLeftDots"
        class="pagination__item points"
      >
        <i class="icon-points" />
      </div>

      <template
        v-for="page in middlePages"
        :key="page"
      >
        <button
          class="pagination__item"
          :class="{ active: page === currentPage }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>
      </template>

      <div
        v-if="showRightDots"
        class="pagination__item points"
      >
        <i class="icon-points" />
      </div>

      <button
        v-if="totalPages > 1"
        class="pagination__item"
        :class="{ active: currentPage === totalPages }"
        @click="changePage(totalPages)"
      >
        {{ totalPages }}
      </button>

      <button
        v-if="totalPages > 1"
        size="large"
        class="move__btn"
        :disabled="isLastPage"
        @click="changePage(currentPage + 1)"
      >
        <ChevronRight />
      </button>
    </div>
    <div class="pagination__total">
      Всего источников: <b>{{ tableStore.pagination.TotalCount }}</b>
      Всего Start URL:  <b>{{ tableStore.pagination.TotalCount }}</b>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, onMounted } from "vue";
import { useTableStore } from "@/stores/table.js";
import ChevronRight from "@/components/icons/ChevronRight.vue";

const props = defineProps({
    take: {
        type: Number,
        required: true,
        default: 14,
    },
    quantityLines: {
        type: Array,
        required: true,
    },
    currentPage: {
        type: Number,
        required: true,
    }
});

const emit = defineEmits(["update:take", "update:currentPage", "update-source-list"]);

const tableStore = useTableStore();

let takeField = ref("");

onMounted(() => {
  takeField.value = props.take;
});

const totalPages = computed(() => tableStore.pagination.TotalPages);
const currentPage = computed(() => tableStore.pagination.CurrentPage);

const isFirstPage = computed(() => currentPage.value === 1);
const isLastPage = computed(() => currentPage.value === totalPages.value);

// Логика для точек и средних страниц
const showLeftDots = computed(() => currentPage.value > 4);
const showRightDots = computed(() => currentPage.value < totalPages.value - 3);

const middlePages = computed(() => {
  const pages = [];

  let start = Math.max(2, currentPage.value - 1);
  let end = Math.min(totalPages.value - 1, currentPage.value + 1);

  if (currentPage.value <= 3) {
    start = 2;
    end = Math.min(4, totalPages.value - 1);
  }

  if (currentPage.value >= totalPages.value - 2) {
    start = Math.max(totalPages.value - 3, 2);
    end = totalPages.value - 1;
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

const changePage = (val) => {
  emit("update:currentPage", val);
  emit("update-source-list");
};

const emitTake = () => {
  emit("update:take", parseInt(takeField.value));
  emit("update-source-list");
};

let debounceTimer = null;

const debounceEmitTake = () => {
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    emitTake();
  }, 2000); // Задержка 2 секунды
};
</script>

<style lang="scss" scoped>
.footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--outline-grey2);
    padding: 10px 25px;
    border-radius: 0 0 8px 8px;
    &__lines {
        display: flex;
        align-items: center;
    }
    &__lines-text {
        font-family: var(--third-family);
        font-weight: 400;
        font-size: 14px;
        line-height: 129%;
        color: var(--text-text-04);
        margin-right: 16px;
    }
}

select {
    background: transparent;
    font-family: var(--font-family);
    font-weight: 500;
    font-size: 14px;
    line-height: 129%;
    color: var(--text-text-01);
    cursor: pointer;
}

.pagination {
    display: flex;
    align-items: center;
    gap: 15px;
    &__item {
        font-family: var(--font-family);
        font-weight: 500;
        font-size: 14px;
        line-height: 129%;
        color: var(--text-text-01);
        min-width: 32px;
        min-height: 32px;
        width: 32px;
        height: 32px;
        transition: .2s;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        line-height: 32px;

        &:not(.active, .points) {
            cursor: pointer;
        }
        &:hover {
            &:not(.active, .points) {
                color: var(--button-focused);
                background: var(--text-01-inverse);
            }
        }
        &.active {
            background: var(--text-01-inverse);
            cursor: default;
        }
        & .icon-points {
            font-size: 13px;
        }
    }
    &__total {
        display: flex;
        gap: 16px;
        color: var(--text-text-04);
        font-size: 14px;
    }
}

.move {
    display: flex;
    gap: 10px;
    align-items: center;
    &__text {
        font-family: var(--font-family);
        font-weight: 400;
        font-size: 14px;
        line-height: 129%;
        color: var(--text-text-04);
    }
    &__field {
        width: 32px;
        display: flex;
        & input {
            width: 100%;
            background: transparent;
            border-bottom: 1px solid #969696;
            line-height: 0;
            font-family: var(--font-family);
            font-weight: 500;
            font-size: 14px;
            color: var(--text-text-04);
            text-align: center;
        }
    }
    &__icon {
        & i {
            font-size: 10px;
        }
    }
    &__btn {
        line-height: 100%;
        padding: 10px 0;
        &:hover {
            &:not(:disabled) {
                & i {
                    color: var(--button-focused);
                }
            }
        }

        &:disabled {
          pointer-events: none;
          color: var(--icon-icon-disabled);
        }

        &.back {
          transform: rotate(180deg);
        }
        & i {
            transition: .2s;
        }
    }
}

.take-field {
    width: 35px;
    height: 31px;
    background: #fff;
    border-radius: 8px;
    text-align: center;
    font-weight: 500;
    font-size: 14px;
    &:hover {
        color: #1F9AFF;
    }
}
</style>
